import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Result, Button, Card, Form, Input, Typography } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getStoredEmailForSignIn,
  isSignInWithEmailLinkAuth,
  completeSignInWithEmailLinkDeduped,
} from '../../api/auth';

type Phase = 'checking' | 'completing' | 'needEmail' | 'error';

/**
 * Handles the email link when user clicks the sign-in link in the registration email.
 * Same browser: uses email saved in localStorage when the link was sent.
 * Other browser/device: asks for email (Firebase-recommended; avoids false "session cleared" errors).
 */
export const CompleteSignUp = () => {
  const { t } = useTranslation(['auth']);
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('checking');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [form] = Form.useForm<{ email: string }>();
  const [submitting, setSubmitting] = useState(false);

  const finishSignIn = useCallback(
    async (email: string, fullUrl: string) => {
      await completeSignInWithEmailLinkDeduped(email, fullUrl);
      navigate('/auth/set-password', { replace: true });
    },
    [navigate]
  );

  useEffect(() => {
    let cancelled = false;
    const fullUrl = window.location.href;

    if (!isSignInWithEmailLinkAuth(fullUrl)) {
      setPhase('error');
      setErrorMessage(t('auth:invalidLink'));
      return;
    }

    const stored = getStoredEmailForSignIn();
    if (stored) {
      setPhase('completing');
      finishSignIn(stored, fullUrl).catch((e) => {
        if (!cancelled) {
          setPhase('error');
          setErrorMessage((e as Error)?.message || t('auth:registerError'));
        }
      });
    } else {
      setPhase('needEmail');
    }

    return () => {
      cancelled = true;
    };
  }, [t, finishSignIn]);

  const onSubmitEmail = async (values: { email: string }) => {
    const fullUrl = window.location.href;
    if (!isSignInWithEmailLinkAuth(fullUrl)) {
      setPhase('error');
      setErrorMessage(t('auth:invalidLink'));
      return;
    }
    try {
      setSubmitting(true);
      await finishSignIn(values.email.trim(), fullUrl);
    } catch (e) {
      setErrorMessage((e as Error)?.message || t('auth:registerError'));
      setPhase('error');
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === 'checking' || phase === 'completing') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5' }}>
        <Spin size="large" tip={phase === 'completing' ? t('auth:completingSignUp') : t('auth:verifyingEmailLink')} />
      </div>
    );
  }

  if (phase === 'needEmail') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5', padding: 24 }}>
        <Card style={{ width: '100%', maxWidth: 420, boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
          <Typography.Title level={4} style={{ marginTop: 0 }}>
            {t('auth:confirmEmailForLinkTitle')}
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
            {t('auth:confirmEmailForLinkSubtitle')}
          </Typography.Paragraph>
          <Form form={form} layout="vertical" onFinish={onSubmitEmail} size="large">
            <Form.Item
              name="email"
              label={t('auth:email')}
              rules={[
                { required: true, message: t('auth:emailRequired') },
                { type: 'email', message: t('auth:emailInvalid') },
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder={t('auth:emailPlaceholder')} autoComplete="email" />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={submitting} block>
                {t('auth:completeSignUpContinue')}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5', padding: 24 }}>
        <Result
          status="error"
          title={t('auth:linkErrorTitle')}
          subTitle={errorMessage}
          extra={
            <Button type="primary" href="/register">
              {t('auth:backToRegister')}
            </Button>
          }
        />
      </div>
    );
  }

  return null;
};
